import React from "react";
import { Button } from "@/components/ui/button";
import { HERO } from "@/constants/testIds";
import { ArrowRight, Sparkles } from "lucide-react";

const FLOW_IMG =
  `${process.env.PUBLIC_URL}/images/claim-flow.webp`;

export const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 bg-grid pointer-events-none [mask-image:radial-gradient(ellipse_at_top,black_40%,transparent_75%)]" />
      <div className="absolute inset-0 radial-glow pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-4xl">
          <div
            data-testid={HERO.badge}
            className="rise inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-3 py-1.5 backdrop-blur"
          >
            <Sparkles size={14} className="text-[#0047FF]" />
            <span className="font-mono-label text-[10px] text-zinc-700">
              AI-Native RCM Platform
            </span>
          </div>

          <h1
            data-testid={HERO.heading}
            className="rise font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter text-zinc-950 leading-[0.98]"
            style={{ animationDelay: "60ms" }}
          >
            Revenue Cycle Management,
            <br className="hidden sm:block" />
            <span className="text-[#0047FF]"> automated end-to-end.</span>
          </h1>

          <p
            className="rise mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed"
            style={{ animationDelay: "120ms" }}
          >
            Zaplee.ai submits clean claims, prevents denials, and accelerates collections
            for healthcare practices, multi-specialty groups and billing companies — all
            from one unified, real-time workspace.
          </p>

          <div
            className="rise mt-9 flex flex-wrap items-center gap-3"
            style={{ animationDelay: "180ms" }}
          >
            <Button
              data-testid={HERO.ctaPrimary}
              onClick={() => scrollTo("contact")}
              className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-full h-12 px-6 text-sm"
            >
              Book a Demo
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
            <Button
              data-testid={HERO.ctaSecondary}
              variant="outline"
              onClick={() => scrollTo("features")}
              className="rounded-full h-12 px-6 text-sm border-zinc-300 hover:bg-zinc-100"
            >
              Explore platform
            </Button>
          </div>

          <div
            className="rise mt-10 flex items-center gap-6 text-xs text-zinc-500"
            style={{ animationDelay: "240ms" }}
          >
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              HIPAA-aligned
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0047FF]" />
              Real-time ingest
            </div>
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-zinc-900" />
              Multi-tenant by design
            </div>
          </div>
        </div>

        {/* Big product visual */}
        <div
          data-testid={HERO.visual}
          className="rise relative mt-16 lg:mt-20"
          style={{ animationDelay: "320ms" }}
        >
          <div className="absolute -inset-x-10 -top-10 bottom-0 -z-10 radial-glow" />
          <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_30px_80px_-30px_rgba(9,9,11,0.25)] overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-zinc-300" />
              <div className="ml-3 font-mono-label text-[10px] text-zinc-500">
                zaplee.ai / dashboard / insurance-claim-flow
              </div>
            </div>
            <img
              src={FLOW_IMG}
              alt="Insurance claim flow visualization"
              className="w-full block"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
